import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import apiClient from "utils/apiClient";

const API_BASE_URL = "/olitask-service/api/v1/app-config";
const AppConfigContext = createContext(null);

const errorMessageFor = (error) => {
  if (error?.response) {
    return error.response.data?.message || `Failed to fetch app config: ${error.response.statusText}`;
  }
  if (error?.request) {
    return "No response from the server. Please check your internet connection.";
  }
  return error?.message || "Unable to fetch app config.";
};

export const AppConfigProvider = ({ children }) => {
  const { authToken, user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const effectiveAuthToken = authToken || sessionStorage.getItem("authToken");
  const actorId = user?.id || user?.userId || "";
  const cacheKey = effectiveAuthToken && selectedWorkspaceId
    ? `${actorId}:${selectedWorkspaceId}`
    : "";
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(new Map());
  const activeKeyRef = useRef(cacheKey);
  const [appConfig, setAppConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  activeKeyRef.current = cacheKey;

  const fetchAppConfig = useCallback(async ({ force = false } = {}) => {
    if (!cacheKey) {
      setAppConfig(null);
      setLoading(false);
      setError(null);
      return null;
    }

    if (!force && cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setAppConfig(cached);
      setLoading(false);
      setError(null);
      return cached;
    }

    setLoading(true);
    setError(null);
    let request = !force ? pendingRef.current.get(cacheKey) : null;
    if (!request) {
      request = apiClient
        .get(API_BASE_URL, { params: { wid: selectedWorkspaceId } })
        .then((response) => response.data)
        .finally(() => {
          if (pendingRef.current.get(cacheKey) === request) pendingRef.current.delete(cacheKey);
        });
      pendingRef.current.set(cacheKey, request);
    }

    try {
      const config = await request;
      cacheRef.current.set(cacheKey, config);
      if (activeKeyRef.current === cacheKey) setAppConfig(config);
      return config;
    } catch (nextError) {
      const message = errorMessageFor(nextError);
      if (activeKeyRef.current === cacheKey) {
        setError(message);
        toast.error(message);
      }
      console.error("fetch app config error:", nextError);
      return null;
    } finally {
      if (activeKeyRef.current === cacheKey) setLoading(false);
    }
  }, [cacheKey, selectedWorkspaceId]);

  useEffect(() => {
    fetchAppConfig();
  }, [fetchAppConfig]);

  const value = useMemo(() => ({
    appConfig,
    error,
    loading,
    refresh: () => fetchAppConfig({ force: true }),
  }), [appConfig, error, fetchAppConfig, loading]);

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfigContext = () => {
  const context = useContext(AppConfigContext);
  if (!context) throw new Error("useAppConfig must be used within AppConfigProvider");
  return context;
};

export default AppConfigContext;
